import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import * as Linking from "expo-linking";
import { Formik } from "formik";
import {
  Box,
  Button,
  Center,
  FormControl,
  Image,
  Input,
  KeyboardAvoidingView,
  Text,
  useTheme,
  VStack,
} from "native-base";
import platform from "platform-detect";
import React from "react";
import { useTranslation } from "react-i18next";
import { ImageBackground, Platform, StyleSheet } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { IS_MOBILE_PLATFORM, Routes } from "../constants";
import { getHostname, getManagedCustomerPortalSubdomain } from "../helpers/userHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import type { ClientAuthenticationMethodEnum } from "../services/backendTypes";
import { getBrandSelector } from "../slices/brandSlice";
import { userSlice } from "../slices/userSlice";

const { useUsersCreateMagicLinkCreateMutation } = backendApi;
const { useUsersCreateLoginSignupPortalCreateMutation } = backendApi;

const magicLinkFormSchema = Yup.object().shape({
  // TODO: This should be refactored to allow use of translated keys
  email: Yup.string().trim().email("Invalid email").required("Required"),
});

export interface MagicLinkFormValues {
  email: string;
}

const APP_STORE_REVIEW_EMAIL = "apple-review@weekmeals.co";
const APP_STORE_REVIEW_ACCOUNT_AUTH_TOKEN = "ee796cdd54495c94762346d5a64eca88f93bf65b";

const PRIVACY_POLICY_URL = "https://nutrition-app.com/privacy";
const TERMS_OF_SERVICE_URL = "https://nutrition-app.com/terms";

const hostname = getHostname();
const subdomainWeAreCurrentlyOn = getManagedCustomerPortalSubdomain(hostname);

type Props = NativeStackScreenProps<RootStackParamList, Routes.LoginScreen>;
const LoginScreen = ({ navigation }: Props): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();

  const dispatch = useDispatch();
  const brand = useSelector(getBrandSelector);

  const [sendMagicLinkToEmailPost] = useUsersCreateMagicLinkCreateMutation();
  // Note: This is doing getOrCreate on user and the sending a magic link (as normal)
  const [sendLoginSignupPost] = useUsersCreateLoginSignupPortalCreateMutation();

  const submitForm = async (values: MagicLinkFormValues): Promise<void> => {
    // NOTE: This is required because app store review cannot log in to the email account
    if (values.email === APP_STORE_REVIEW_EMAIL) {
      dispatch(userSlice.actions.storeAuthToken(APP_STORE_REVIEW_ACCOUNT_AUTH_TOKEN));
    }

    // NOTE: This needs to be a "valid" url to pass validation on the backend
    const redirectUrlString = Platform.OS === "web" ? "/" : "login.me";

    // NOTE: This is for debugging the compiled app
    // alert(`redirectUrlString: ${redirectUrlString}`);

    const redirectUrl = Linking.createURL(redirectUrlString);
    // NOTE: This is for debugging the compiled app
    // alert(`redirectUrl: ${redirectUrl}`);

    // NOTE: This is for debugging the compiled app
    // alert(`magicLinkToEmailPostBody: ${JSON.stringify(magicLinkToEmailPostBody, null, 2)}`);

    let authenticationMethod: ClientAuthenticationMethodEnum = "MAGIC_LINK";

    try {
      if (subdomainWeAreCurrentlyOn && !IS_MOBILE_PLATFORM) {
        // Note: This is doing getOrCreate on user and the sending a magic link (as normal)
        const response = await sendLoginSignupPost({
          loginSignupPortalRequest: {
            email: values.email,
            redirect_url: redirectUrl,
            organisation_subdomain: subdomainWeAreCurrentlyOn,
          },
        }).unwrap();

        authenticationMethod = response?.authentication_method;
      } else {
        await sendMagicLinkToEmailPost({
          magicLinkCreateRequest: {
            email: values.email,
            redirect_url: redirectUrl,
          },
        }).unwrap();
      }
    } catch (e: unknown) {
      // Type guard to check if the error matches expected structure
      interface BackendError {
        data?: {
          error?: string;
        };
      }

      const err = e as BackendError;
      // Check if it's a backend error response
      if (err.data?.error) {
        // TODO: Replace alert with proper error handling
        // eslint-disable-next-line no-alert
        alert(err.data.error);
      } else {
        // TODO: Replace alert with proper error handling
        // eslint-disable-next-line no-alert
        alert(t("login_screen.failure_recovery"));
      }
      return;
    }

    // Only navigate to the sent screen if there has been no error
    switch (authenticationMethod) {
      case "OTP":
        navigation.navigate(Routes.EnterOTPScreen, { email: values.email });
        break;
      default:
        navigation.navigate(Routes.MagicLinkSentScreen, { email: values.email });
    }
  };

  const initialValues: MagicLinkFormValues = { email: "" };

  const handleLinkPress = async (url: string): Promise<void> => {
    try {
      if (Platform.OS === "web") {
        // Open in new tab on web
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        // Use Linking for mobile platforms
        await Linking.openURL(url);
      }
    } catch (linkError) {
      // eslint-disable-next-line no-console
      console.error("Failed to open URL:", linkError);
    }
  };

  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
  const loginBackgroundImage =
    subdomainWeAreCurrentlyOn === "foodwithferne"
      ? // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
        require("../resources/foodwithferene_login_screen_background.jpg")
      : // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
        require("../resources/login_screen_background.jpg");

  return (
    <Box flex={1} bg="black" position="relative">
      <ImageBackground source={loginBackgroundImage} style={styles.backgroundImage} resizeMode="cover">
        {/* Dark overlay for better text readability */}
        <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(44, 30, 20, 0.6)" />

        <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : "height"} style={styles.keyboardView}>
          <Formik initialValues={initialValues} validationSchema={magicLinkFormSchema} onSubmit={submitForm}>
            {({ isSubmitting, handleChange, handleBlur, handleSubmit, values, errors, touched, dirty, isValid }) => (
              <Center flex={1} px="8">
                <Box safeArea={true} w="100%" maxW="350" flex={1}>
                  <VStack flex={1} alignItems="center">
                    {/* App Branding Section */}
                    <VStack space={4} alignItems="center" flex={1} justifyContent="center" mt={platform.web ? 12 : 24}>
                      {/* Organization Logo */}
                      <Image
                        source={{ uri: brand?.logo }}
                        alt="Organization Logo"
                        width="150"
                        height="150"
                        resizeMode="contain"
                      />

                      {/* <Heading size="2xl" fontWeight="bold" color="white" textAlign="center" fontFamily="body">
                        {t("login_screen.heading")}
                      </Heading> */}
                      {/* <Text fontSize="lg" color="white" textAlign="center" opacity={0.9} fontWeight="medium">
                        {t("login_screen.subtitle")}
                      </Text> */}
                    </VStack>

                    {/* Email Input */}
                    <VStack space={4} w="100%" flex={1} justifyContent="flex-end" pb="4">
                      <FormControl isRequired w="100%">
                        <Input
                          onChangeText={handleChange("email")}
                          onBlur={handleBlur("email")}
                          value={values.email}
                          placeholder={t("login_screen.login_form.email_placeholder")}
                          testID={"magicLinkEmail-input"}
                          variant="unstyled"
                          py="4"
                          px="4"
                          size="lg"
                          autoCapitalize="none"
                          autoComplete="email"
                          keyboardType="email-address"
                          textContentType="emailAddress"
                          borderRadius={theme.borderRadii.input.default}
                          bg="function.10"
                          fontSize="16"
                          isInvalid={!!(errors.email && touched.email)}
                          shadow={5}
                          borderWidth={errors.email && touched.email ? "2" : "0"}
                          borderColor={errors.email && touched.email ? "red.500" : "transparent"}
                          _focus={{
                            bg: "function.10",
                            shadow: 7,
                            borderColor: errors.email && touched.email ? "red.500" : "transparent",
                          }}
                          _invalid={{
                            bg: "function.10",
                            borderColor: "red.500",
                          }}
                          placeholderTextColor="gray.500"
                        />
                      </FormControl>
                    </VStack>

                    {/* Sign In Button at Bottom */}
                    <VStack space={4} w="100%" mb={40}>
                      <Button
                        isLoading={isSubmitting}
                        isDisabled={!dirty || !isValid}
                        onPress={() => handleSubmit()}
                        testID="magicLinkButton"
                        bg="primary.40"
                        _pressed={{ bg: "primary.50", shadow: 7 }}
                        _disabled={{ bg: "gray.400" }}
                        borderRadius={theme.borderRadii.button.default}
                        size="lg"
                        h={12}
                        w="100%"
                        shadow={5}
                        _text={{
                          fontSize: "18",
                          fontWeight: "semibold",
                        }}
                        _loading={{
                          h: 12,
                          minH: 12,
                        }}
                      >
                        {t("login_screen.send_magic_link_button_text")}
                      </Button>
                    </VStack>

                    {/* Terms and Privacy */}
                    <VStack space={2} alignItems="center" mt="6" mb="8">
                      <Box flexDirection="row" flexWrap="wrap" justifyContent="center">
                        <Text fontSize="sm" color="function.10" opacity={0.8}>
                          {t("login_screen.terms_and_privacy_policy_intro")}{" "}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="function.10"
                          textDecorationLine="underline"
                          opacity={0.9}
                          onPress={() => {
                            handleLinkPress(TERMS_OF_SERVICE_URL).catch((linkError) => {
                              // eslint-disable-next-line no-console
                              console.error("Failed to open Terms URL:", linkError);
                            });
                          }}
                        >
                          {t("login_screen.terms_of_service_link_text")}
                        </Text>
                        <Text fontSize="sm" color="function.10" opacity={0.8}>
                          {" "}
                          {t("login_screen.and_text")}{" "}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="function.10"
                          textDecorationLine="underline"
                          opacity={0.9}
                          onPress={() => {
                            handleLinkPress(PRIVACY_POLICY_URL).catch((linkError) => {
                              // eslint-disable-next-line no-console
                              console.error("Failed to open Privacy URL:", linkError);
                            });
                          }}
                        >
                          {t("login_screen.privacy_policy_link_text")}
                        </Text>
                      </Box>
                    </VStack>
                  </VStack>
                </Box>
              </Center>
            )}
          </Formik>
        </KeyboardAvoidingView>
      </ImageBackground>
    </Box>
  );
};

const styles = StyleSheet.create({
  backgroundImage: {
    flex: 1,
  },
  keyboardView: {
    flex: 1,
  },
});
export default LoginScreen;
